import type { ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'

export type BadgeTone =
  | 'neutral'
  | 'brand'
  | 'navy'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'teal'

const toneClass: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-100 text-neutral-600',
  brand: 'bg-brand-50 text-brand-700',
  navy: 'bg-navy-50 text-navy-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  info: 'bg-info-50 text-info-700',
  teal: 'bg-teal-50 text-teal-700',
}

// Solid tone fills, shared by the badge dot and the accent strip on record cards.
export const toneSolidClass: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-400',
  brand: 'bg-brand-500',
  navy: 'bg-navy-700',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  info: 'bg-info-500',
  teal: 'bg-teal-500',
}

export function Badge({
  children,
  tone = 'neutral',
  dot = true,
  className,
}: {
  children: ReactNode
  tone?: BadgeTone
  dot?: boolean
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold',
        toneClass[tone],
        className
      )}
    >
      {dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', toneSolidClass[tone])} aria-hidden="true" />}
      {children}
    </span>
  )
}

export function IconTile({
  children,
  tone = 'brand',
  className,
}: {
  children: ReactNode
  tone?: BadgeTone
  className?: string
}) {
  return (
    <span
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
        toneClass[tone],
        className
      )}
    >
      {children}
    </span>
  )
}
